import React, { CSSProperties, ReactElement, useState } from 'react';
import { AiLabel } from '@spa/pages-converters/cms/components/convertCmsImage';
import { ImageAiMarkerComponent } from '@spa/components/image-ai-marker/image-ai-marker.component';
import ImageComponent from '@spa/components/image-component/image.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import styles from './youtube-player.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';

export interface Props {
  readonly videoId: string;
  readonly previewImageSrc: string | null;
  readonly previewImageAiLabel?: AiLabel | null;
  readonly alt: string;
  readonly height: number;
  readonly width: number;
  readonly autoplay: boolean;
  readonly muted: boolean;
}

export default function YoutubePlayerComponent({
  videoId,
  previewImageSrc,
  previewImageAiLabel,
  alt,
  height,
  width,
  autoplay,
  muted
}: Props): ReactElement {
  const initialVideoLoaded: boolean = autoplay;
  const [videoLoaded, setVideoLoaded] = useState<boolean>(initialVideoLoaded);
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('video-player');
  const { formatMessage: formatAiMarkerMessage }: NamespacedIntl =
    useNamespacedIntl('image-ai-marker');

  const previewImageAiLabelEnabled = previewImageAiLabel?.enabled === true;
  const previewDisplayAlt =
    previewImageAiLabelEnabled && alt ? `${alt}, ${formatAiMarkerMessage('accessibleLabel')}` : alt;
  const digitalSourceTypeAttr = previewImageAiLabelEnabled
    ? { 'data-digital-source-type': 'compositeWithTrainedAlgorithmicMedia' }
    : {};

  const videoSrc: string = `https://www.youtube.com/embed/${videoId}?autoplay=${
    autoplay ? '1' : '0'
  }&mute=${muted || autoplay ? '1' : '0'}`;

  const videoSrcUrl: URL = new URL(`https://www.youtube.com/embed/${videoId}`);
  videoSrcUrl.searchParams.set('autoplay', autoplay ? '1' : '0');
  videoSrcUrl.searchParams.set('mute', muted || autoplay ? '1' : '0');

  const wrapperStyle: CSSProperties = {
    maxWidth: `${width}px`,
    maxHeight: `${height}px`
  };

  return (
    <div className={styles['youtube-player']}>
      <div className={styles['youtube-player__video-frame']} style={wrapperStyle}>
        {videoLoaded || !previewImageSrc ? (
          <div className={styles['youtube-player__iframe-wrapper']}>
            <iframe
              className={styles['youtube-player__iframe']}
              src={videoSrcUrl.toString()}
              data-src={videoSrc}
              width={width}
              height={height}
              title={alt}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-presentation"
              aria-label={alt}
            ></iframe>
          </div>
        ) : (
          <>
            <button
              className={styles['youtube-player__preview-wrapper']}
              onClick={(): void => setVideoLoaded(true)}
              aria-label={formatMessage('play')}
            >
              <ImageComponent
                className={styles['youtube-player__preview']}
                src={previewImageSrc}
                alt={previewDisplayAlt}
                fill={true}
                loading="lazy"
                {...digitalSourceTypeAttr}
              />
              <div className={styles['youtube-player__play-button']}>
                <ImageComponent
                  src={'/assets/icons/play.svg'}
                  alt={formatMessage('play')}
                  width={50}
                  height={50}
                  loading="lazy"
                />
              </div>
            </button>
            {previewImageAiLabel && (
              <ImageAiMarkerComponent
                show={previewImageAiLabel.enabled}
                position={previewImageAiLabel.position}
                style={previewImageAiLabel.style}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
